import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ModalBody, Form, FormGroup, Label, ModalFooter } from 'reactstrap'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useLocalization } from '@/hooks/localization'
import { Dropzone } from '@/components/fileAttachments/dropzone'
import context from '@/context'
import { DropDownList } from '@/components/dropDownList'
import { Button } from '@/components/button'
import { CancelToken } from '@/utils/fetch'
import { processDesignerReport } from '@/store/utils/reportUtils'
import LoadingIndicator from '@/components/LoadingIndicator'
import { EnumDropDownList } from '@/components/enumDropDownList'
import { reportType } from '@/enums/reportType'

const ReportTemplateOpenDialog = React.memo(function ReportTemplateOpenDialog (props) {
  const { onAction } = props
  const { localize, localizeProps } = useLocalization()
  const [selectedReportType, setReportType] = useState(0)
  const [template, setTemplate] = useState(null)
  const [version, setVersion] = useState(null)
  const [error, setError] = useState(null)
  const versionId = version && version.id
  const [loading, setLoading] = useState(false)
  const cancellation = useRef(CancelToken.source())
  const onReportTypeChange = useCallback(e => {
    setReportType(e.value)
    setTemplate(null)
    setVersion(null)
  }, [])
  const onTemplateChange = useCallback(e => {
    const template = e.item
    setTemplate(template)
    setVersion(template ? { id: template.id, name: `${template.version}` } : null)
  }, [])
  const onVersionChange = useCallback(e => {
    const { id, version: name } = template
    setVersion(e.item || { id: id, name })
  }, [template])
  const onDrop = useCallback(([file]) => {
    setLoading(true)
    const reader = new window.FileReader()
    reader.onabort = () => {
      setError('file reading was aborted')
      setLoading(false)
    }
    reader.onerror = () => {
      setError('file reading has failed')
      setLoading(false)
    }
    reader.onload = () => {
      if (file.type === 'application/json') {
        try {
          onAction(processDesignerReport(JSON.parse(reader.result)))
        } catch (err) {
          setLoading(false)
          setError(`${err}`)
        }
      } else {
        // XML
        context.api.reportTemplateService.convertFromXml({ xml: reader.result })
          .then(data => onAction(processDesignerReport(data)))
          .catch((err) => {
            setLoading(false)
            setError(`${err}`)
          })
      }
    }
    reader.readAsText(file)
  }, [onAction])

  const templateId = template ? template.reportTemplateId : null
  const loadTemplates = useCallback((req, options) => {
    return context.api.reportTemplateService.getTemplates(selectedReportType, allAtOnce(req), options)
  }, [selectedReportType])
  const loadVersion = useCallback((req, options) => {
    return context.api.reportTemplateService.getVersions(templateId, req, options)
  }, [templateId])

  const onSubmit = useCallback(() => {
    if (template) {
      setLoading(true)
      context.api.reportTemplateService.exportTemplate(versionId, { cancelToken: cancellation.current.token })
        .then(processDesignerReport)
        .then(onAction)
        .catch((err) => {
          setLoading(false)
          setError(`${err}`)
        })
    }
  }, [onAction, template, versionId])
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cancellation.current.cancel('dialog closed')
    }
  }, [])
  const dropzoneAccept = useMemo(() => ['application/json', 'application/xml', 'text/xml'], [])
  return (
    <React.Fragment>
      <BasicHeader title='ReportDesigner.OpenDialog' {...props} />
      {loading
        ? <ModalBody><LoadingIndicator /></ModalBody>
        : <ModalBody>
          <ErrorBoundary>
            <Form>
              <FormGroup>
                <Label>{localize('ReportDesigner.OpenFromSystem')}</Label>
                <EnumDropDownList value={selectedReportType} onChange={onReportTypeChange} enumDefinition={reportType} />
              </FormGroup>
              <FormGroup>
                <Label>{localize('ReportDesigner.OpenFromSystem')}</Label>
                <DropDownList value={template} valueField='id' onChange={onTemplateChange} dataSource={loadTemplates}
                  groupBy={localizeProps('category', 'latinCategory')} refreshValue={selectedReportType} />
              </FormGroup>
              {!!version && <FormGroup>
                <Label>{localize('ReportDesigner.OpenFromSystem')}</Label>
                <DropDownList value={version} textField='name' valueField='id' onChange={onVersionChange} dataSource={loadVersion} />
              </FormGroup>}
              {!!error && <FormGroup>
                <div className='error-boundary'>
                  {error}
                </div>
              </FormGroup>}
              <FormGroup>
                <div className='pull-right' style={{ paddingTop: 5 }}>
                  <Button color='primary' disabled={!version} onClick={onSubmit}>{localize('Common.Open')}</Button>
                </div>
              </FormGroup>
            </Form>
          </ErrorBoundary>
        </ModalBody>
      }
      {!loading && <ModalFooter>
        <Form style={{ width: '100%' }}>
          <FormGroup>
            <Label>{localize('ReportDesigner.OpenFile')}</Label>
            <Dropzone
              uploadButtonText={'Common.DragAndDropFile'}
              multiple={false}
              accept={dropzoneAccept}
              onDropAccepted={onDrop}
            />
          </FormGroup>
        </Form>
      </ModalFooter>}
    </React.Fragment>
  )
})

function allAtOnce (req) {
  delete req.skip
  delete req.take
  req.sort = [{ field: 'order', dir: 'asc' }]
  return req
}

export default {
  component: ReportTemplateOpenDialog
}



// WEBPACK FOOTER //
// ./src/components/reportDesigner/dialogs/openDialog.js